/**
 * Import spritesheet (spec 034): one sprite's dialog. The author drops a
 * sprite tool's export (a .zip or .png); each animation it holds shows as a
 * row that becomes a new state or fills an existing one. Nothing is stored
 * until "Add to n states" (D-1); Cancel and Escape upload nothing.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '../../api/client';
import { useSceneStore } from '../../stores/sceneStore';
import { SheetImportError } from '../../../../Shared/src/sheets/sheetImport';
import { pictureSlug } from '../../../../Shared/src/pictures/pictureMap';
import type { SpriteSize } from '../../../../Shared/src/@types';
import {
  commitSheetImport, readSheetExport, uniqueStateName, uploadSheetForPicture, type ImportRow, type ImportTarget, type ReadExport,
} from '../../utils/sheetImportFlow';
import styles from './SheetImportDialog.module.scss';

export interface SheetImportDialogProps {
  /** The sprite whose states the rows become (034); absent in expression mode. */
  spriteId?: string;
  onClose: () => void;
  /** Spec 035: the state a single animation fills by default (the States panel's card). */
  targetState?: string;
  /**
   * Spec 035 D-5: expression mode (the Pictures view). One animation fills
   * this expression: when the file holds several, the dialog asks which.
   * `onImported` gets the uploaded sheet; the caller fills the expression.
   */
  expression?: { subject: string; label: string; onImported: (mediaId: number, size: SpriteSize) => Promise<string | null> };
}

const seconds = (ms: number) => `${Number((ms / 1000).toFixed(1))} s`;

/** A Becomes value: `fill:<state>` or `new`. */
type Becomes = string;

const STILL = 48;

const Still: React.FC<{ row: ImportRow }> = ({ row }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (typeof URL.createObjectURL !== 'function') return undefined;
    const u = URL.createObjectURL(row.still.blob);
    setUrl(u);
    return () => URL.revokeObjectURL?.(u);
  }, [row.still.blob]);
  const { rect, size } = row.still;
  const k = STILL / Math.max(1, rect.w, rect.h);
  return (
    <div className={styles.still} aria-hidden>
      <div
        data-testid="sheet-import-still"
        style={{
          width: rect.w * k,
          height: rect.h * k,
          backgroundImage: url ? `url(${url})` : undefined,
          backgroundSize: `${size.width * k}px ${size.height * k}px`,
          backgroundPosition: `${-rect.x * k}px ${-rect.y * k}px`,
          backgroundRepeat: 'no-repeat',
        }}
      />
    </div>
  );
};

const SheetImportDialog: React.FC<SheetImportDialogProps> = ({ spriteId, onClose, targetState, expression }) => {
  const sprite = useSceneStore((s) => (spriteId ? s.scene?.sprites.find((sp) => sp.id === spriteId) : undefined));
  const [chosen, setChosen] = useState<string | null>(null);
  const stateNames = useMemo(() => sprite?.states.map((s) => s.name) ?? [], [sprite]);

  const [read, setRead] = useState<ReadExport | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [include, setInclude] = useState<Record<string, boolean>>({});
  const [becomes, setBecomes] = useState<Record<string, Becomes>>({});
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const closed = useRef(false);
  // Reset on (re)mount: StrictMode mounts, unmounts and mounts again.
  useEffect(() => {
    closed.current = false;
    return () => { closed.current = true; };
  }, []);

  const takeFile = useCallback(async (file: File | undefined) => {
    if (!file) return;
    setRefusal(null);
    setFailure(null);
    setRead(null);
    try {
      const result = await readSheetExport(file);
      if (closed.current) return;
      setRead(result);
      setInclude(Object.fromEntries(result.rows.map((r) => [r.id, true])));
      setChosen(result.rows.length === 1 ? result.rows[0].id : null);
      // Starts on the state whose pictureSlug is the animation's ("Talking" fills for "talking");
      // a single animation opened from a state's card fills that state (spec 035).
      const single = result.rows.length === 1 && targetState !== undefined && stateNames.includes(targetState);
      setBecomes(Object.fromEntries(result.rows.map((r) => {
        if (single) return [r.id, `fill:${targetState}`];
        const match = stateNames.find((n) => pictureSlug(n) === pictureSlug(r.name));
        return [r.id, match !== undefined ? `fill:${match}` : 'new'];
      })));
    } catch (err) {
      if (closed.current) return;
      setRefusal(err instanceof SheetImportError ? err.message : `This file could not be read${err instanceof Error && err.message ? `: ${err.message}` : ''}`);
    }
  }, [stateNames, targetState]);

  /** Each included row's target; new names made unique against the sprite's states and each other. */
  const picks = useMemo(() => {
    if (!read) return [] as { row: ImportRow; target: ImportTarget }[];
    const taken = [...stateNames];
    return read.rows.filter((r) => include[r.id]).map((row) => {
      const value = becomes[row.id] ?? 'new';
      if (value.startsWith('fill:')) return { row, target: { kind: 'fill', state: value.slice(5) } as ImportTarget };
      const name = uniqueStateName(row.name, taken);
      taken.push(name);
      return { row, target: { kind: 'new', name } as ImportTarget };
    });
  }, [read, include, becomes, stateNames]);

  const clash = useMemo(() => {
    const seen = new Set<string>();
    for (const p of picks) {
      if (p.target.kind !== 'fill') continue;
      if (seen.has(p.target.state)) return p.target.state;
      seen.add(p.target.state);
    }
    return null;
  }, [picks]);

  const newName = (row: ImportRow) => uniqueStateName(row.name, stateNames);

  const confirm = useCallback(async () => {
    if (expression) {
      const row = read?.rows.find((r) => r.id === chosen);
      if (!row) return;
      setBusy(true);
      setFailure(null);
      try {
        const { mediaId, size } = await uploadSheetForPicture(apiClient, expression.subject, row);
        const refused = await expression.onImported(mediaId, size);
        if (refused) throw new Error(refused);
        if (!closed.current) onClose();
      } catch (err) {
        if (!closed.current) setFailure(err instanceof Error ? err.message : String(err));
      } finally {
        if (!closed.current) setBusy(false);
      }
      return;
    }
    if (picks.length === 0 || clash || !spriteId) return;
    setBusy(true);
    setFailure(null);
    try {
      await commitSheetImport(apiClient, spriteId, picks);
      if (!closed.current) onClose();
    } catch (err) {
      if (!closed.current) setFailure(err instanceof Error ? err.message : String(err));
    } finally {
      if (!closed.current) setBusy(false);
    }
  }, [picks, clash, spriteId, onClose, expression, read, chosen]);

  const n = picks.length;

  return (
    <div className={styles.dialog} data-testid="sheet-import-dialog">
      <div className={styles.main}>
        <div
          className={`${styles.drop} ${over ? styles.over : ''}`}
          data-testid="sheet-import-drop"
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); void takeFile(e.dataTransfer?.files?.[0]); }}
        >
          <span>Drop a spritesheet export here (.zip or .png)</span>
          <button type="button" className={styles.button} onClick={() => input.current?.click()} disabled={busy}>
            Choose file…
          </button>
          <input
            ref={input}
            type="file"
            accept=".zip,.png,application/zip,image/png"
            hidden
            data-testid="sheet-import-file"
            onChange={(e) => { void takeFile(e.target.files?.[0]); e.target.value = ''; }}
          />
        </div>

        {refusal && <div className={styles.error} role="alert">{refusal}</div>}

        {read && (
          <>
            <div className={styles.fileInfo}>
              <strong>{read.fileName}</strong>
              <span className={styles.muted}>{read.summary}</span>
            </div>
            <div className={styles.rows}>
              {read.rows.map((row) => (
                <div className={styles.row} key={row.id} data-testid="sheet-import-row">
                  {expression ? (
                    <input
                      type="radio"
                      name="sheet-import-one"
                      aria-label={`Use ${row.name}`}
                      checked={chosen === row.id}
                      onChange={() => setChosen(row.id)}
                      disabled={busy}
                    />
                  ) : (
                  <input
                    type="checkbox"
                    aria-label={`Include ${row.name}`}
                    checked={!!include[row.id]}
                    onChange={(e) => setInclude((s) => ({ ...s, [row.id]: e.target.checked }))}
                    disabled={busy}
                  />
                  )}
                  <Still row={row} />
                  <span className={styles.name}>{row.name}</span>
                  <span>{row.cut.frameCount} frames · {seconds(row.durationMs)} · {row.cut.fps} fps</span>
                  <span>{row.cut.columns} × {row.cut.rows}</span>
                  {!expression && (
                  <select
                    aria-label={`${row.name} becomes`}
                    value={becomes[row.id] ?? 'new'}
                    onChange={(e) => setBecomes((s) => ({ ...s, [row.id]: e.target.value }))}
                    disabled={busy || !include[row.id]}
                  >
                    {stateNames.map((name) => (
                      <option key={name} value={`fill:${name}`}>Fills state “{name}”</option>
                    ))}
                    <option value="new">New state “{newName(row)}”</option>
                  </select>
                  )}
                </div>
              ))}
              {read.unknown.map((u) => (
                <div className={`${styles.row} ${styles.unknownRow}`} key={u.path} data-testid="sheet-import-unknown">
                  <div className={styles.still} />
                  <span>
                    <span className={styles.name}>{u.name}</span>{' '}
                    <span className={styles.muted}>Grid unknown · Pick it from Media to cut it by hand</span>
                  </span>
                </div>
              ))}
            </div>
            {expression && read.rows.length > 1 && (
              <p className={styles.muted} data-testid="sheet-import-which">Which animation fills {expression.label}?</p>
            )}
            {clash && <div className={styles.error} role="alert">Two animations fill “{clash}”; pick another state for one of them.</div>}
          </>
        )}

        {failure && <div className={styles.error} role="alert">Nothing was added: {failure}</div>}
      </div>

      <aside className={styles.side}>
        <h4>What it reads</h4>
        <ul>
          <li>A sheet PNG with its TexturePacker JSON (Sheet + JSON from Aseprite, Phaser, PixiJS, Ludo.ai), one or several in a .zip</li>
          <li>A sheet PNG with its grid in the name, such as <code>walk_6x2.png</code> or Ludo.ai’s <code>-frames-8-rows-2-cols-4</code></li>
          <li>An animation pack: a .zip with one folder per animation</li>
          <li>All frames: numbered frame PNGs, one folder per animation</li>
        </ul>
        <p className={styles.muted}>GIF and video are not read; export a spritesheet instead.</p>
      </aside>

      <div className={styles.footer}>
        <button type="button" className={`${styles.button} ${styles.secondary}`} onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.button}
          onClick={() => void confirm()}
          disabled={expression ? busy || !chosen : busy || n === 0 || !!clash || !sprite}
          data-testid="sheet-import-confirm"
        >
          {failure ? 'Try again' : expression ? `Use for ${expression.label}` : `Add to ${n} ${n === 1 ? 'state' : 'states'}`}
        </button>
      </div>
    </div>
  );
};

export default SheetImportDialog;
